/** Typed runtime settings. The server enforces the type of each setting's default (bool / int / float / str).
 * `SettingOut` has no explicit type field yet (requested in docs/ws/WS7-requests.md), and JSON.parse turns a
 * Python float default like `15.0` into the JS number 15, so the raw JSON is parsed with a reviver that reads
 * the source text of each `default` (JSON.parse source-text access; falls back to integer-vs-fraction). */
import { useQuery } from "@tanstack/react-query";
import { api, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";

export type SettingType = "bool" | "int" | "float" | "str" | "json";
export type TypedSetting = Schemas["SettingOut"] & { type: SettingType };

type ReviverCtx = { source?: string } | undefined;

export function inferType(def: unknown, floatSource = false): SettingType {
  if (typeof def === "boolean") return "bool";
  if (typeof def === "number") return floatSource || !Number.isInteger(def) ? "float" : "int";
  if (typeof def === "string" || def == null) return "str";
  return "json";
}

/** Parse GET /api/settings JSON text into typed settings. */
export function parseSettings(text: string): TypedSetting[] {
  let lastDefaultFloat = false;
  const reviver = function (this: unknown, key: string, value: unknown, ctx?: ReviverCtx): unknown {
    if (key === "default") {
      lastDefaultFloat = typeof value === "number" && !!ctx?.source && /[.eE]/.test(ctx.source);
    } else if (value && typeof value === "object" && !Array.isArray(value) && "key" in value && "default" in value) {
      const v = value as Schemas["SettingOut"] & { type?: unknown };
      const explicit = typeof v.type === "string" && ["bool", "int", "float", "str", "json"].includes(v.type);
      return { ...v, type: explicit ? v.type : inferType(v.default, lastDefaultFloat) };
    }
    return value;
  };
  const parsed: unknown = JSON.parse(text, reviver as (this: unknown, key: string, value: unknown) => unknown);
  return Array.isArray(parsed) ? (parsed as TypedSetting[]) : [];
}

export async function fetchSettings(): Promise<TypedSetting[]> {
  const text = await unwrap(api.GET("/api/settings", { parseAs: "text" }));
  return parseSettings(text);
}

export function useSettings() {
  return useQuery({ queryKey: qk.settings, queryFn: fetchSettings });
}

export type ParseResult = { ok: true; value: unknown } | { ok: false; error: string };

/** Input text (or checkbox state) → the JSON value to send, or a validation message. */
export function parseSettingInput(type: SettingType, raw: string | boolean): ParseResult {
  if (type === "bool") return { ok: true, value: raw === true || raw === "true" };
  const s = String(raw).trim();
  if (type === "int") {
    return /^-?\d+$/.test(s) ? { ok: true, value: Number(s) } : { ok: false, error: "must be a whole number" };
  }
  if (type === "float") {
    const n = Number(s);
    return s !== "" && Number.isFinite(n) ? { ok: true, value: n } : { ok: false, error: "must be a number" };
  }
  if (type === "json") {
    try {
      return { ok: true, value: JSON.parse(s) };
    } catch {
      return { ok: false, error: "must be valid JSON" };
    }
  }
  return { ok: true, value: String(raw) };
}

export function settingInputText(type: SettingType, v: unknown): string {
  if (type === "json") return JSON.stringify(v);
  return v == null ? "" : String(v);
}

export function groupSettings(list: TypedSetting[]): [string, TypedSetting[]][] {
  const m = new Map<string, TypedSetting[]>();
  for (const s of list) m.set(s.group, [...(m.get(s.group) ?? []), s]);
  return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
}
